import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, HelpCircle, MessageCircle, ChevronRight, X } from 'lucide-react';

export default function SupportPage() {
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'technical', message: '' });

  const load = async () => { try { const { data } = await api.get('/support'); setTickets(data.tickets); } catch (e) {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.subject || !form.message) return toast.error(lang === 'it' ? 'Compila tutti i campi' : 'Fill all fields');
    try { await api.post('/support', form); toast.success(lang === 'it' ? 'Ticket creato!' : 'Ticket created!'); setShowNew(false); setForm({ subject: '', category: 'technical', message: '' }); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  const statusColors = { open: 'bg-green-100 text-green-700', in_progress: 'bg-blue-100 text-blue-700', resolved: 'bg-gray-100 text-gray-600', closed: 'bg-gray-200 text-gray-500' };
  const statusLabels = { open: lang === 'it' ? 'Aperto' : 'Open', in_progress: lang === 'it' ? 'In Lavorazione' : 'In Progress', resolved: lang === 'it' ? 'Risolto' : 'Resolved', closed: lang === 'it' ? 'Chiuso' : 'Closed' };
  const catLabels = { technical: lang === 'it' ? 'Problema Tecnico' : 'Technical', statements: lang === 'it' ? 'Rendiconti/Pagamenti' : 'Statements/Payments', modification: lang === 'it' ? 'Modifica Release' : 'Release Modification', other: lang === 'it' ? 'Altro' : 'Other' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-pn-dark">{lang === 'it' ? 'Supporto' : 'Support'}</h1>
        <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2"><Plus size={18} />{lang === 'it' ? 'Nuovo Messaggio' : 'New Message'}</button>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4"><h2 className="font-bold text-lg">{lang === 'it' ? 'Nuovo Ticket' : 'New Ticket'}</h2><button onClick={() => setShowNew(false)}><X size={20} /></button></div>
            <div className="space-y-4">
              <div><label className="block text-xs font-medium mb-1">{lang === 'it' ? 'Oggetto' : 'Subject'} *</label><input className="input-field" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} /></div>
              <div><label className="block text-xs font-medium mb-1">{lang === 'it' ? 'Categoria' : 'Category'}</label><select className="input-field" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>{Object.entries(catLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div><label className="block text-xs font-medium mb-1">{lang === 'it' ? 'Messaggio' : 'Message'} *</label><textarea className="input-field h-32 resize-y" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} /></div>
            </div>
            <div className="flex gap-3 mt-6"><button onClick={() => setShowNew(false)} className="btn-secondary flex-1">{lang === 'it' ? 'Annulla' : 'Cancel'}</button><button onClick={create} className="btn-primary flex-1">{lang === 'it' ? 'Invia' : 'Send'}</button></div>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div> : tickets.length === 0 ? (
        <div className="card p-12 text-center"><HelpCircle size={48} className="mx-auto mb-4 text-gray-300" /><p className="text-gray-500">{lang === 'it' ? 'Nessun ticket' : 'No tickets'}</p></div>
      ) : (
        <div className="space-y-3">{tickets.map(t => (
          <Link key={t.id} to={`/support/${t.id}`} className="card p-4 flex items-center gap-4 hover:shadow-md transition">
            <MessageCircle size={20} className="text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0"><h3 className="font-medium truncate">{t.subject}</h3><p className="text-xs text-gray-400">{catLabels[t.category]} · {new Date(t.created_at).toLocaleDateString()}</p></div>
            <span className={`badge ${statusColors[t.status]}`}>{statusLabels[t.status]}</span>
            {t.unread > 0 && <span className="w-5 h-5 bg-pn-red text-white text-xs rounded-full flex items-center justify-center">{t.unread}</span>}
            <ChevronRight size={16} className="text-gray-300" />
          </Link>
        ))}</div>
      )}
    </div>
  );
}
